import React, { useState, useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, FileText, BookOpen, Trash2, X, RefreshCw,
    ChevronRight, Plus, CheckCircle, AlertCircle
} from 'lucide-react';
import { teacherContext } from '../services/api';

/**
 * TeacherContextPanel — allows teachers to upload and manage their
 * instructional context documents (scope & sequence, pacing guides,
 * syllabi, course objectives). These documents inform AI generation.
 */
const TeacherContextPanel = ({ selectedSubject = '', subjects = [], autoOpenUpload = false, onAutoOpenHandled }) => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [filterSubject, setFilterSubject] = useState(selectedSubject);

    // Auto-open upload panel when triggered from the nudge banner
    useEffect(() => {
        if (autoOpenUpload) {
            setShowUpload(true);
            if (onAutoOpenHandled) onAutoOpenHandled();
        }
    }, [autoOpenUpload, onAutoOpenHandled]);

    const fetchDocs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterSubject) params.subject = filterSubject;
            const res = await teacherContext.list(params);
            setDocs(res.data.documents || []);
        } catch (err) {
            console.error('Failed to load teacher context:', err);
        } finally {
            setLoading(false);
        }
    }, [filterSubject]);

    useEffect(() => { fetchDocs(); }, [fetchDocs]);

    // Sync with parent's selected subject
    useEffect(() => {
        if (selectedSubject) setFilterSubject(selectedSubject);
    }, [selectedSubject]);

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this document from your teaching context?')) return;
        try {
            await teacherContext.delete(id);
            fetchDocs();
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const typeLabels = {
        scope_sequence: 'Scope & Sequence',
        pacing_guide: 'Pacing Guide',
        syllabus: 'Syllabus',
        course_objectives: 'Course Objectives',
        other: 'Other'
    };

    const typeColors = {
        scope_sequence: 'bg-blue-50 text-blue-600',
        pacing_guide: 'bg-violet-50 text-violet-600',
        syllabus: 'bg-emerald-50 text-emerald-600',
        course_objectives: 'bg-amber-50 text-amber-600',
        other: 'bg-slate-50 text-slate-600'
    };

    return (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <BookOpen size={14} className="text-amber-500" />
                    My Course Context
                </h4>
                <button
                    onClick={() => setShowUpload(!showUpload)}
                    className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                >
                    <Plus size={12} />
                    Add
                </button>
            </div>

            <p className="text-[11px] text-slate-400 mb-3">
                Upload your scope & sequence, pacing guide, or syllabus to personalize AI-generated lessons.
            </p>

            {/* Subject Filter Tabs */}
            {subjects.length > 1 && (
                <div className="flex gap-1 mb-3 flex-wrap">
                    <button
                        onClick={() => setFilterSubject('')}
                        className={`text-[10px] px-2 py-1 rounded-full font-medium transition-colors ${!filterSubject ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                    >
                        All
                    </button>
                    {subjects.map(subj => (
                        <button
                            key={subj}
                            onClick={() => setFilterSubject(subj)}
                            className={`text-[10px] px-2 py-1 rounded-full font-medium transition-colors ${filterSubject === subj ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                        >
                            {subj}
                        </button>
                    ))}
                </div>
            )}

            {/* Upload Panel */}
            <AnimatePresence>
                {showUpload && (
                    <ContextUploadPanel
                        onClose={() => setShowUpload(false)}
                        onSuccess={() => { setShowUpload(false); fetchDocs(); }}
                        defaultSubject={filterSubject || selectedSubject}
                        subjects={subjects}
                    />
                )}
            </AnimatePresence>

            {/* Document List */}
            {loading ? (
                <div className="flex justify-center py-6">
                    <RefreshCw size={18} className="animate-spin text-slate-300" />
                </div>
            ) : docs.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <FileText size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-xs text-slate-400">No context uploaded yet</p>
                    <button
                        onClick={() => setShowUpload(true)}
                        className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium"
                    >
                        Upload your first document →
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {docs.map(doc => (
                        <div
                            key={doc.id}
                            className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 group"
                        >
                            <FileText size={14} className="text-slate-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">
                                    {doc.title || doc.filename || 'Untitled'}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColors[doc.doc_type] || typeColors.other
                                        }`}>
                                        {typeLabels[doc.doc_type] || doc.doc_type}
                                    </span>
                                    {doc.subject && (
                                        <span className="text-[10px] text-slate-400">{doc.subject}</span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => handleDelete(doc.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all"
                                title="Remove"
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const ContextUploadPanel = ({ onClose, onSuccess, defaultSubject = '', subjects = [] }) => {
    const [title, setTitle] = useState('');
    const [docType, setDocType] = useState('scope_sequence');
    const [subject, setSubject] = useState(defaultSubject);
    const [gradeLevel] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);
    const fileInputId = 'teacher-context-upload-file';

    const acceptFile = (selectedFile) => {
        if (!selectedFile) return;
        const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt'];
        const normalizedName = selectedFile.name.toLowerCase();
        const isAllowed = allowedExtensions.some(ext => normalizedName.endsWith(ext));

        if (!isAllowed) {
            alert('Please upload a PDF, DOCX, DOC, or TXT file.');
            return;
        }

        setFile(selectedFile);
        if (!title) {
            const titleFromName = selectedFile.name.replace(/\.[^.]+$/, '').trim();
            if (titleFromName) setTitle(titleFromName);
        }
    };

    const handleFileChange = (e) => {
        acceptFile(e.target.files?.[0]);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        acceptFile(e.dataTransfer.files?.[0]);
    };

    const handleUpload = async () => {
        if (!title || !file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('doc_type', docType);
            if (subject) formData.append('subject', subject);
            if (gradeLevel) formData.append('grade_level', gradeLevel);
            formData.append('file', file);
            await teacherContext.upload(formData);
            onSuccess();
        } catch (err) {
            console.error('Upload failed:', err);
            alert('Upload failed: ' + (err.response?.data?.detail || err.message));
        } finally {
            setUploading(false);
        }
    };

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-3"
        >
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-amber-700">Add Course Context</span>
                    <button onClick={onClose} className="text-amber-400 hover:text-amber-600">
                        <X size={14} />
                    </button>
                </div>

                <div className="space-y-2">
                    <input
                        type="text"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Title (e.g., 7th Grade Math S&S)"
                        className="w-full px-2.5 py-1.5 rounded-lg border border-amber-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                    />

                    <div className="grid grid-cols-2 gap-2">
                        <select
                            value={docType}
                            onChange={e => setDocType(e.target.value)}
                            className="px-2.5 py-1.5 rounded-lg border border-amber-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                        >
                            <option value="scope_sequence">Scope & Sequence</option>
                            <option value="pacing_guide">Pacing Guide</option>
                            <option value="syllabus">Syllabus</option>
                            <option value="course_objectives">Course Objectives</option>
                            <option value="other">Other</option>
                        </select>
                        {subjects.length > 0 ? (
                            <select
                                value={subject}
                                onChange={e => setSubject(e.target.value)}
                                className="px-2.5 py-1.5 rounded-lg border border-amber-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                            >
                                <option value="">All Subjects</option>
                                {subjects.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={subject}
                                onChange={e => setSubject(e.target.value)}
                                placeholder="Subject (optional)"
                                className="px-2.5 py-1.5 rounded-lg border border-amber-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                            />
                        )}
                    </div>


                    <label
                        htmlFor={fileInputId}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`block px-2.5 py-2 rounded-lg border border-dashed text-[11px] cursor-pointer transition-colors text-center ${
                            dragOver
                                ? 'border-amber-500 bg-amber-100 text-amber-700'
                                : 'border-amber-300 text-amber-600 hover:bg-white'
                        }`}
                    >
                        {file ? `📄 ${file.name}` : 'Click to select file (.pdf, .docx, .txt)'}
                    </label>
                    <input
                        ref={fileRef}
                        id={fileInputId}
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.docx,.doc,.txt"
                    />

                    <button
                        onClick={handleUpload}
                        disabled={!title || !file || uploading}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                        {uploading ? 'Uploading…' : 'Save Context'}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default TeacherContextPanel;
